import fetchAllOndcVendors from '@dehaat/kisan-app-bl/api/fetchAllOndcVendors';
import { FULFILLMENT_TYPE, Vendor } from '@dehaat/kisan-app-bl/models/Vendor';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CLIENT_NAME_COOKIE, COOKIE_MAX_AGE_1YEAR, CURRENT_VENDOR_KEY, ONDC_VENDOR_ID_COOKIE, ONDC_VENDOR_KEY, VENDOR_ID_COOKIE_NAME } from '@/constants/common';
import CurrentLocation from '@/models/Location';
import { getCookieValue, isFromAgcApp, setLocalStorageKey } from '@/utils/helper';
import getVendors from '@/utils/helpers/getVendors';
import { RootState } from '../index';

// ONDC providers are only needed on /ondc routes, so they are fetched
// separately from the DeHaat vendors used by the regular ordering flow.
export const fetchOndcVendors = createAsyncThunk('location/fetchOndcVendors', async ({
  latLng,
  language
}: {
  latLng: string;
  language?: string;
}) => {
  const ondcVendors = await fetchAllOndcVendors(latLng, language);
  setLocalStorageKey(ONDC_VENDOR_KEY, ondcVendors.length > 0 ? ondcVendors[0] : null);
  document.cookie = `${ONDC_VENDOR_ID_COOKIE}=${ondcVendors.length > 0 ? ondcVendors[0].id : null}; max-age=${COOKIE_MAX_AGE_1YEAR}; path=/`;
  return ondcVendors;
});
export const fetchHyperlocalData = createAsyncThunk('location/fetchHyperlocalData', async ({
  latLng,
  language,
  vendorId,
  isCSCUser = false,
  forCreditOrder = false
}: {
  latLng: string;
  language?: string;
  vendorId?: string;
  isCSCUser?: boolean;
  forCreditOrder?: boolean;
}) => {
  const dehaatVendors = await getVendors(latLng, language, undefined, undefined, forCreditOrder);
  let currentVendor: Vendor | null = null;
  if (dehaatVendors.length > 0) {
    const clientName = getCookieValue(CLIENT_NAME_COOKIE);
    const availableVendor = vendorId && !isFromAgcApp(clientName) ? dehaatVendors.find(v => v.id == vendorId) : undefined;
    if (vendorId && availableVendor) {
      currentVendor = availableVendor;
    } else {
      const filteredVendors = dehaatVendors?.filter(vendor => vendor?.fulfillment_type === FULFILLMENT_TYPE.BOTH || vendor?.fulfillment_type === FULFILLMENT_TYPE.DOORSTEP_DELIVERY);
      currentVendor = isCSCUser && filteredVendors.length > 0 ? filteredVendors[0] : dehaatVendors[0];
    }
    setLocalStorageKey(CURRENT_VENDOR_KEY, currentVendor);
    document.cookie = `${VENDOR_ID_COOKIE_NAME}=${currentVendor ? currentVendor.id : null}; max-age=${COOKIE_MAX_AGE_1YEAR}; path=/`;
  } else {
    setLocalStorageKey(CURRENT_VENDOR_KEY, null);
    document.cookie = `${VENDOR_ID_COOKIE_NAME}=${null}; max-age=${COOKIE_MAX_AGE_1YEAR}; path=/`;
  }
  return {
    currentVendor,
    dehaatVendors
  };
});
export interface LocationSlice {
  loadingHyperlocalData: boolean;
  currentLocation?: CurrentLocation;
  currentVendor?: Vendor | null;
  ondcVendor?: Vendor | null;
  vendorsList: Vendor[];
  ondcVendorsList: Vendor[];
  // latLng the ONDC providers were last fetched for
  ondcVendorsLatLng?: string;
}
const initialState: LocationSlice = {
  loadingHyperlocalData: true,
  vendorsList: [],
  ondcVendorsList: []
};
const locationSlice = createSlice({
  name: 'location',
  initialState,
  reducers: {
    setLoadingHyperlocalData: (state, action: PayloadAction<boolean>) => {
      state.loadingHyperlocalData = action.payload;
    },
    setCurrentLocation: (state, action: PayloadAction<CurrentLocation>) => {
      state.currentLocation = action.payload;
    },
    setCurrentVendor: (state, action: PayloadAction<Vendor>) => {
      state.currentVendor = action.payload;
    },
    setOndcVendor: (state, action: PayloadAction<Vendor>) => {
      state.ondcVendor = action.payload;
    },
    setVendorsList: (state, action: PayloadAction<Vendor[]>) => {
      state.vendorsList = action.payload;
    },
    setOndcVendorsList: (state, action: PayloadAction<Vendor[]>) => {
      state.ondcVendorsList = action.payload;
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchHyperlocalData.pending, state => {
      // Only block with the full-screen loader on the first load. If vendor data
      // is already present (re-entering the shop in the same session), refresh
      // silently in the background so the user keeps seeing content instead of a
      // full-screen loader flashing on every visit. The fetch still runs and
      // updates the vendor/list state on `fulfilled`.
      if (state.vendorsList.length === 0) {
        state.loadingHyperlocalData = true;
      }
    });
    builder.addCase(fetchHyperlocalData.fulfilled, (state, action) => {
      const {
        currentVendor,
        dehaatVendors
      } = action.payload;
      state.currentVendor = currentVendor;
      state.vendorsList = dehaatVendors;
      state.loadingHyperlocalData = false;
    });
    builder.addCase(fetchHyperlocalData.rejected, state => {
      state.currentVendor = null;
      state.vendorsList = [];
      state.loadingHyperlocalData = false;
    });
    builder.addCase(fetchOndcVendors.fulfilled, (state, action) => {
      const ondcVendors = action.payload;
      state.ondcVendor = ondcVendors.length > 0 ? ondcVendors[0] : null;
      state.ondcVendorsList = ondcVendors;
      state.ondcVendorsLatLng = action.meta.arg.latLng;
    });
    builder.addCase(fetchOndcVendors.rejected, (state, action) => {
      state.ondcVendor = null;
      state.ondcVendorsList = [];
      state.ondcVendorsLatLng = action.meta.arg.latLng;
    });
  }
});
export const {
  setLoadingHyperlocalData,
  setCurrentLocation,
  setCurrentVendor,
  setOndcVendor,
  setVendorsList,
  setOndcVendorsList
} = locationSlice.actions;
export const selectLoadingHyperlocalData = (state: RootState) => state.location.loadingHyperlocalData;
export const selectLatLng = (state: RootState) => state.location.currentLocation?.latLng;
export const selectCurrentLocation = (state: RootState) => state.location.currentLocation;
export const selectCurrentVendor = (state: RootState) => state.location.currentVendor;
export const selectCurrentVendorId = (state: RootState) => {
  return state.location.currentVendor ? state.location.currentVendor.id.toString() : undefined;
};
export const selectOndcVendor = (state: RootState) => state.location.ondcVendor;
export const selectOndcVendorId = (state: RootState) => state.location.ondcVendor ? state.location.ondcVendor.id.toString() : undefined;
export const selectVendorsList = (state: RootState) => state.location.vendorsList;
export const selectOndcVendorsList = (state: RootState) => state.location.ondcVendorsList;
// ONDC pages wait on this instead of loadingHyperlocalData: true until the
// ONDC providers have been fetched for the current location.
export const selectLoadingOndcVendors = (state: RootState) => {
  const latLng = state.location.currentLocation?.latLng;
  return state.location.loadingHyperlocalData || !!latLng && state.location.ondcVendorsLatLng !== latLng;
};
export default locationSlice;